/**
 * Capability detection (src/platform). All signals come from feature detection,
 * never browser-name (Consistency Conventions). Story 1.1 consumes only `webgl`
 * and `reducedMotion`; `storage` is detected for later stories. Every read is
 * defensive: a missing or throwing browser API yields `false`, never an error.
 */
export interface Capabilities {
  /** Whether a WebGL (or WebGL2) context can be created. Startup never requires it (AD-3). */
  readonly webgl: boolean;
  /** Whether the OS prefers reduced motion. */
  readonly reducedMotion: boolean;
  /** Whether localStorage is usable. Non-load-bearing (AD-7). */
  readonly storage: boolean;
}

function detectWebGL(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl');
    return gl != null;
  } catch {
    return false;
  }
}

function detectReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function detectStorage(): boolean {
  try {
    const key = '__kepler_probe__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** Detect all capabilities once, defensively. */
export function detectCapabilities(): Capabilities {
  return {
    webgl: detectWebGL(),
    reducedMotion: detectReducedMotion(),
    storage: detectStorage()
  };
}
